import { MAX_ROUND_POINTS, ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from '@okwhere/shared';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Logo } from '../../components/Logo';
import { Card, ErrorBanner, PageShell } from '../../components/PageShell';
import { ProfileEditor } from '../../components/ProfileEditor';
import { ThemePicker } from '../../components/ThemePicker';
import { Button } from '../../components/ui/Button';
import { formatDateTime, formatPoints } from '../../lib/format';
import { draftProfile, loadPartyOptions, loadSettings, saveProfile } from '../../lib/storage';
import { useAccess } from '../access/access';
import { AccessCard } from '../access/AccessCard';
import { usePartyStore } from '../party/store';

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, type: 'spring' as const, stiffness: 200, damping: 24 },
});

export function HomePage() {
  const navigate = useNavigate();
  const createParty = usePartyStore((state) => state.create);
  const activeParty = usePartyStore((state) => state.snapshot?.code ?? null);
  const [profile, setProfile] = useState(draftProfile);
  const [code, setCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ready, locked, role, expiresAt } = useAccess();

  const ensureProfile = () => {
    if (!profile.name.trim()) {
      setError('Pick a nickname first — your friends need to know who to blame.');
      document.getElementById('nickname')?.focus();
      return false;
    }
    saveProfile({ ...profile, name: profile.name.trim() });
    return true;
  };

  const handleCreate = async () => {
    if (!ensureProfile()) return;
    setCreating(true);
    setError(null);
    try {
      const roomCode = await createParty({ ...profile, name: profile.name.trim() }, loadSettings(), loadPartyOptions());
      navigate(`/party/${roomCode}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the party.');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.length !== ROOM_CODE_LENGTH) return setError(`Party codes have ${ROOM_CODE_LENGTH} letters.`);
    if (!ensureProfile()) return;
    navigate(`/party/${code}`);
  };

  return (
    <PageShell header={false} className="flex min-h-full flex-col items-center pt-10 sm:pt-16">
      <div className="absolute top-4 right-4 z-10 sm:top-6 sm:right-6">
        <ThemePicker />
      </div>
      <motion.div {...fadeUp(0)} className="flex flex-col items-center text-center">
        <Logo size="xl" animated />
        <p className="mt-5 max-w-lg text-lg text-balance text-ink-300 sm:text-xl">
          Dropped somewhere on Earth. Look around, drop a pin, and settle who really knows the world.
        </p>
      </motion.div>

      {activeParty && (
        <motion.div {...fadeUp(0.05)} className="mt-6">
          <Link
            to={`/party/${activeParty}`}
            className="glass flex items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold transition hover:bg-ink-800"
          >
            <span className="size-2 animate-pulse rounded-full bg-success-400" />
            You're in party <span className="font-display tracking-widest text-highlight-400">{activeParty}</span> — back to it →
          </Link>
        </motion.div>
      )}

      <motion.div {...fadeUp(0.1)} className="mt-10 w-full max-w-xl">
        <Card>
          <ProfileEditor
            value={profile}
            onChange={(next) => {
              setProfile(next);
              setError(null);
            }}
          />
        </Card>
      </motion.div>

      <AnimatePresence initial={false}>
        {locked && <AccessCard key="access" className="mt-4 w-full max-w-4xl" />}
      </AnimatePresence>

      <div className="mt-4 grid w-full max-w-4xl gap-4 md:grid-cols-2">
        <motion.div {...fadeUp(0.18)}>
          <Card className="flex h-full flex-col">
            <ModeHeader emoji="🧭" title="Solo" subtitle="Your pace, your maps. Chase your best score." />
            <div className="mt-auto pt-6">
              <Button
                size="lg"
                className="w-full"
                disabled={locked}
                onClick={() => {
                  if (profile.name.trim()) saveProfile({ ...profile, name: profile.name.trim() });
                  navigate('/solo');
                }}
              >
                Play solo
              </Button>
            </div>
          </Card>
        </motion.div>

        <motion.div {...fadeUp(0.24)}>
          <Card className="flex h-full flex-col">
            <ModeHeader emoji="🎉" title="Party" subtitle="Same places for everyone, live leaderboard, up to 20 friends." />
            <div className="mt-auto flex flex-col gap-3 pt-6">
              <Button size="lg" variant="success" className="w-full" loading={creating} disabled={locked} onClick={handleCreate}>
                Create a party
              </Button>
              <form onSubmit={handleJoin} className="flex gap-2">
                <input
                  value={code}
                  onChange={(event) =>
                    setCode(
                      event.target.value
                        .toUpperCase()
                        .split('')
                        .filter((char) => ROOM_CODE_ALPHABET.includes(char))
                        .join('')
                        .slice(0, ROOM_CODE_LENGTH),
                    )
                  }
                  placeholder="CODE"
                  aria-label="Party code"
                  className="h-11 w-full min-w-0 rounded-2xl bg-ink-850 px-4 text-center font-display text-lg font-extrabold tracking-[0.4em] uppercase ring-1 ring-white/10 outline-none placeholder:tracking-[0.3em] placeholder:text-ink-600 focus:ring-2 focus:ring-success-400"
                />
                <Button type="submit" variant="secondary" disabled={code.length !== ROOM_CODE_LENGTH}>
                  Join
                </Button>
              </form>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Only once we know: a visitor without a code shouldn't see it flash by. */}
      {ready && !locked && (
        <motion.div {...fadeUp(0.28)} className="mt-4 w-full max-w-4xl">
          <Card className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex-1">
              <ModeHeader emoji="📅" title="Daily challenge" subtitle="The same 5 places for everyone today. One try, then compare." />
            </div>
            <Link to="/daily" className="sm:w-56">
              <Button size="lg" variant="secondary" className="w-full">
                Play today's →
              </Button>
            </Link>
          </Card>
        </motion.div>
      )}

      <ErrorBanner message={error} className="mt-4 w-full max-w-4xl" />

      {role && (
        <motion.p {...fadeUp(0.3)} className="mt-5 text-center text-sm text-ink-400">
          {role === 'owner' ? (
            <>
              🔑 Owner access ·{' '}
              <Link to="/access" className="font-semibold text-info-400 hover:underline">
                Guest codes →
              </Link>
            </>
          ) : (
            <>🎟️ Guest access until {formatDateTime(expiresAt!)}</>
          )}
        </motion.p>
      )}

      <motion.ol {...fadeUp(0.32)} className="mt-14 grid w-full max-w-4xl gap-6 text-sm text-ink-300 sm:grid-cols-3">
        {[
          ['👀', 'Look around', 'Signs, plates, plants, which side people drive on — every clue counts.'],
          ['📍', 'Drop a pin', 'Open the map and place your guess. No take-backs.'],
          ['🏆', 'Score', `Up to ${formatPoints(MAX_ROUND_POINTS)} points per round. The closer, the better.`],
        ].map(([emoji, title, text], index) => (
          <li key={title} className="flex gap-3">
            <span className="text-2xl">{emoji}</span>
            <div>
              <p className="font-display font-bold text-paper">
                {index + 1}. {title}
              </p>
              <p className="mt-0.5">{text}</p>
            </div>
          </li>
        ))}
      </motion.ol>
    </PageShell>
  );
}

function ModeHeader({ emoji, title, subtitle }: { emoji: string; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-4">
      <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-ink-800 text-3xl ring-1 ring-white/8">{emoji}</span>
      <div>
        <h2 className="font-display text-2xl font-extrabold">{title}</h2>
        <p className="mt-1 text-ink-300">{subtitle}</p>
      </div>
    </div>
  );
}
